import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { AccountDetailResponse, AccountListResponse, AccountOverrides } from '@/lib/types'

interface AccountListParams {
  page?: number
  per_page?: number
  search?: string
  status?: string
  tier?: string
  has_overrides?: boolean
}

const ACCOUNTS_KEY = ['accounts'] as const
const ACCOUNT_KEY = (id: string): readonly string[] => ['account', id]

export function useAccounts(
  params?: AccountListParams,
): ReturnType<typeof useQuery<AccountListResponse>> {
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, params],
    queryFn: () =>
      apiClient.get<AccountListResponse>(
        endpoints.accounts.list({
          page: params?.page,
          per_page: params?.per_page,
          search: params?.search,
          status: params?.status,
          tier: params?.tier,
        }),
      ),
  })
}

export function useAccount(id: string): ReturnType<typeof useQuery<AccountDetailResponse>> {
  return useQuery({
    queryKey: ACCOUNT_KEY(id),
    queryFn: () => apiClient.get<AccountDetailResponse>(endpoints.accounts.detail(id)),
    enabled: !!id,
  })
}

export function usePauseAccount(): ReturnType<
  typeof useMutation<unknown, unknown, { id: string; reason: string }>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.post(endpoints.accounts.pause(id), { reason }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY })
      void qc.invalidateQueries({ queryKey: ACCOUNT_KEY(id) })
    },
  })
}

export function useResumeAccount(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.post(endpoints.accounts.resume(id)),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY })
      void qc.invalidateQueries({ queryKey: ACCOUNT_KEY(id) })
    },
  })
}

export function useChangeTier(): ReturnType<
  typeof useMutation<unknown, unknown, { id: string; tier: string }>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, tier }: { id: string; tier: string }) =>
      apiClient.patch(endpoints.accounts.tier(id), { tier }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY })
      void qc.invalidateQueries({ queryKey: ACCOUNT_KEY(id) })
    },
  })
}

export function useUpdateOverrides(): ReturnType<
  typeof useMutation<unknown, unknown, { id: string; overrides: AccountOverrides }>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, overrides }: { id: string; overrides: AccountOverrides }) =>
      apiClient.put(endpoints.accounts.overrides(id), overrides),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY })
      void qc.invalidateQueries({ queryKey: ACCOUNT_KEY(id) })
    },
  })
}

export function useClearOverrides(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.del(endpoints.accounts.overrides(id)),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY })
      void qc.invalidateQueries({ queryKey: ACCOUNT_KEY(id) })
    },
  })
}

export function useGrantCredits(): ReturnType<
  typeof useMutation<unknown, unknown, { id: string; amount: number; reason: string }>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, amount, reason }: { id: string; amount: number; reason: string }) =>
      apiClient.post(endpoints.accounts.credits(id), { amount, reason }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ACCOUNT_KEY(id) })
    },
  })
}

export function useCreatePilot(): ReturnType<
  typeof useMutation<unknown, unknown, { id: string; notes: string; expires_at: string }>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, notes, expires_at }: { id: string; notes: string; expires_at: string }) =>
      apiClient.post(endpoints.accounts.createPilot(id), { notes, expires_at }),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY })
      void qc.invalidateQueries({ queryKey: ACCOUNT_KEY(id) })
    },
  })
}
